import type { GameObj } from 'kaplay'
import type { KAPLAYCtx } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { getTiledSize, parsePolygon, getFileCacheStr } from '../utils.ts'
import { useGameUiTextFrame } from '../objects/game-ui-text-frame.ts'

export function useFloor09(k: KAPLAYCtx) {
  const cacheStr = getFileCacheStr()
  k.loadSprite('overlay', `sprites/overlay.png?${cacheStr}`, {
    sliceX: 4,
    sliceY: 2,
    anims: {
      question: {
        frames: [0],
      },
    },
  })
  k.loadSprite('floor09', `sprites/floors/floor-09.png?${cacheStr}`)
  k.loadSprite('floor09Hover', `sprites/floors/floor-09--hover.png?${cacheStr}`)
  const collidersJson = k.loadJSON('floor09Colliders', `colliders/floor-09.parsed.json?${cacheStr}`)

  const { showTextFrame } = useGameUiTextFrame(k)
  const { getLangString } = useLang(k)

  function getQuestionObj() {
    return [
      k.sprite('overlay', { anim: 'question' }),
      k.area({ isSensor: true }),
      k.layer('background'),
      'sfx_emit_action',
      'floor_09_info',
      'info',
    ]
  }

  function setFloor09() {
    const floor = k.add()

    k.onLoad(() => {
      const collidersData = collidersJson.data

      floor.add([
        k.sprite('floor09', {
          width: getTiledSize(collidersData.width),
          height: getTiledSize(collidersData.height),
        }),
        k.layer('background'),
        'floor_09_background',
        'background',
      ])

      floor.add([
        k.sprite('floor09Hover', {
          width: getTiledSize(collidersData.width),
          height: getTiledSize(collidersData.height),
        }),
        k.layer('hover'),
        'floor_09_hover',
        'hover',
      ])

      collidersData.objects.forEach((obj: any) => {
        const poly = parsePolygon(k, obj)
        floor.add([k.pos(), k.area({ shape: poly }), k.body({ isStatic: true }), 'colliders'])
      })

      floor.add([
        k.level(
          [
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                     r           ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                 s               ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                  N              ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                       y                                             7           ',
            '                                      p                      9                   ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                 c                     8         ',
            '      e                                4                                         ',
            '                 1                         a                  v                  ',
            '                                                                                 ',
            '                                                                 5               ',
            '                               3        u  b                                     ',
            '                                                                                 ',
            '               2                                                                 ',
            '         z                                               u                       ',
            '                       o                             p                           ',
            '                           #                                                     ',
            '                                                                          q      ',
            '                    $                                             6         $    ',
            '              f                                                                  ',
            '                           #                                                     ',
            '                                                             t           &       ',
            '                                                                                 ',
            '              g                                                  d               ',
            '                           #                                                     ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                           #                                                     ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
          ],
          {
            tileWidth: getTiledSize(),
            tileHeight: getTiledSize(),
            tiles: {

              // Elevators
              '#': () => [
                k.rect(getTiledSize(), getTiledSize(1.5), { fill: false }),
                k.pos(k.vec2(getTiledSize(0.5), 0)),
                k.area({ isSensor: true }),
                'floor_09_elevator',
                'elevator',
              ],
              '&': () => [
                k.rect(getTiledSize(2), getTiledSize(1), { fill: false }),
                k.pos(k.vec2(0, getTiledSize(0.5))),
                k.area({ isSensor: true }),
                'floor_09_elevator',
                'elevator',
              ],
              '$': () => [
                k.rect(getTiledSize(2), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_stairs',
                'stairs',
              ],

              // Doors
              '1': () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_wellbeing_room',
                'floor_09_door',
                'door',
              ],
              '2': () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_wc',
                'floor_09_door',
                'door',
              ],
              '3': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.pos(getTiledSize(0.5), 0),
                k.area({ isSensor: true }),
                'floor_09_door_to_meeting_room_e',
                'floor_09_door',
                'door',
              ],
              '4': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_focus_room',
                'floor_09_door',
                'door',
              ],
              '5': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_treadmill_room',
                'floor_09_door',
                'door',
              ],
              '6': () => [
                k.rect(getTiledSize(2), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_wc_handicap',
                'floor_09_door',
                'door',
              ],
              '7': () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_wc_female',
                'floor_09_door',
                'door',
              ],
              '8': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_wc_male',
                'floor_09_door',
                'door',
              ],
              '9': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_multifaith_room',
                'floor_09_door',
                'door',
              ],
              a: () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.pos(getTiledSize(0.5), 0),
                k.area({ isSensor: true }),
                'floor_09_door_to_meeting_room_g',
                'floor_09_door',
                'door',
              ],
              b: () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.pos(getTiledSize(0.5), 0),
                k.area({ isSensor: true }),
                'floor_09_door_to_meeting_room_h',
                'floor_09_door',
                'door',
              ],
              c: () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_meeting_room_i',
                'floor_09_door',
                'door',
              ],
              d: () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_meeting_room_f',
                'floor_09_door',
                'door',
              ],
              e: () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_09_door_to_medical_room',
                'floor_09_door',
                'door',
              ],
              f: () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.pos(getTiledSize(0.5), 0),
                k.area({ isSensor: true }),
                'floor_09_door_to_focus_room_b_a',
                'floor_09_door',
                'door',
              ],
              g: () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.pos(getTiledSize(0.5), 0),
                k.area({ isSensor: true }),
                'floor_09_door_to_focus_room_b_b',
                'floor_09_door',
                'door',
              ],

              // Interactions
              // z: () => [
              //   ...getQuestionObj(),
              //   'floor_09_info_project_space',
              // ],
              y: () => [
                ...getQuestionObj(),
                'floor_09_info_coffee_point',
              ],
              /*
              x: () => [
                ...getQuestionObj(),
                '',
              ],
              w: () => [
                ...getQuestionObj(),
                '',
              ],
              */
              v: () => [
                ...getQuestionObj(),
                'floor_09_info_comms_room',
              ],
              u: () => [
                ...getQuestionObj(),
                'floor_09_info_semi_enclosed',
              ],
              t: () => [
                ...getQuestionObj(),
                'floor_09_info_lockers',
              ],
              s: () => [
                ...getQuestionObj(),
                'floor_09_info_pods',
              ],
              r: () => [
                ...getQuestionObj(),
                'floor_09_info_concentration_workstations',
              ],
              q: () => [
                ...getQuestionObj(),
                'floor_09_info_alt_access',
              ],

              p: () => [
                ...getQuestionObj(),
                'floor_09_info_communication_workstations',
              ],

              o: () => [
                ...getQuestionObj(),
                'floor_09_info_intro',
              ],

              // Easter Egg
              N: () => [
                k.rect(getTiledSize(), getTiledSize(1.5), { fill: false }),
                k.area({ isSensor: true }),
                k.layer('background'),
                'floor_09_pc_easteregg',
                'easteregg',
              ],
            },
          },
        ),
        k.layer('objects'),
        'floor_09',
        'floor',
      ])

      // Info
      k.onCollide('floor_09_info', 'player', (info: GameObj) => {
        if (info.tags.includes('floor_09_info_coffee_point'))
          showTextFrame(getLangString('coffee_point_info'))
        else if (info.tags.includes('floor_09_info_intro'))
          showTextFrame(getLangString('floor_09_info_intro'))
        else if (info.tags.includes('floor_09_info_comms_room'))
          showTextFrame(getLangString('comms_room_info'))
        else if (info.tags.includes('floor_09_info_lockers'))
          showTextFrame(getLangString('lockers_info'))
        else if (info.tags.includes('floor_09_info_pods'))
          showTextFrame(getLangString('pods_info'))
        else if (info.tags.includes('floor_09_info_semi_enclosed'))
          showTextFrame(getLangString('semi_enclosed_info'))
        else if (info.tags.includes('floor_09_info_concentration_workstations'))
          showTextFrame(getLangString('concentration_workstations_info'))
        else if (info.tags.includes('floor_09_info_communication_workstations'))
        showTextFrame(getLangString('communication_workstations_info'))
        else if (info.tags.includes('floor_09_info_alt_access'))
          showTextFrame(getLangString('alt_access_info'))
      })
      k.onCollideEnd('player', 'info', () => {
        showTextFrame()
      })

    })

    return floor
  }

  return {
    setFloor09,
  }
}
